import {useEffect, useRef} from 'react'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToHtmlString} from '@contentful/rich-text-html-renderer'
import type {GenericContent, GenericCTAGroup} from '../../../../../../brand/lib/types/contentful'
import {animateFadeIn, type AnimationElement} from '../../_utils/animations'
import {animateParallax, type AnimationParallaxElement} from '../../_utils/parallax'

import {Heading, Prose, Text} from '@primer/react-brand'
import {CtaButtons} from '../../_shared/CtaButtons/CtaButtons'
import {Label} from '../../_shared/Label/Label'
import {Spacer} from '../../_shared/Spacer/Spacer'
import {MediaAsset} from '../../_shared/MediaAsset/MediaAsset'

import Styles from './River.module.css'

type Props = {
  content: GenericContent
  location: string
  index: number
  sectionIndex: number
}

export function River(props: Props) {
  const {content, index, location, sectionIndex} = props
  const {htmlId, heading, subheading, label, text, ctas, media} = content.fields

  const direction = htmlId // Used for setting left/right alignment

  const title = heading
  const subTitle = subheading ? documentToPlainTextString(subheading) : ''

  const eyebrow = label?.fields.text
  const description = text ? documentToHtmlString(text) : ''

  const ctaGroup = ctas?.at(0) as GenericCTAGroup

  // Animation
  const wrapper1Ref = useRef<HTMLDivElement>(null)
  const wrapper2Ref = useRef<HTMLDivElement>(null)

  const headingRef = useRef<HTMLDivElement>(null)
  const subheadingRef = useRef<HTMLDivElement>(null)
  const descriptionRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLDivElement>(null)
  const assetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const elements1: AnimationElement[] = []
    if (headingRef.current) elements1.push({element: headingRef.current})
    if (subheadingRef.current) elements1.push({element: subheadingRef.current})
    if (descriptionRef.current) elements1.push({element: descriptionRef.current})
    if (ctaRef.current) elements1.push({element: ctaRef.current})
    const cleanup1 = wrapper1Ref.current ? animateFadeIn(elements1, wrapper1Ref.current) : undefined

    const elements2: AnimationParallaxElement[] = []
    if (assetRef.current) elements2.push({element: assetRef.current})
    const cleanup2 = wrapper2Ref.current ? animateParallax(elements2, wrapper2Ref.current) : undefined

    return () => {
      cleanup1?.()
      cleanup2?.()
    }
  }, [])

  return (
    <div className={Styles.River} data-direction={direction || (index % 2 === 0 ? 'left' : 'right')}>
      <div className={Styles.Cell} ref={wrapper1Ref}>
        <div className={Styles.Content}>
          {eyebrow && (
            <>
              <Label text={eyebrow} />
              <Spacer size="64px" size1280="0" />
            </>
          )}

          {title && (
            <Heading data-heading="2" as={sectionIndex === 0 ? 'h2' : 'h3'} ref={headingRef}>
              {title}
            </Heading>
          )}

          <div>
            {subTitle && (
              <>
                <Spacer size="32px" size1280="0" />
                <div ref={subheadingRef}>
                  <Text className={Styles.SubTitle} weight="semibold">
                    {subTitle}
                  </Text>
                </div>
              </>
            )}

            {description && (
              <>
                <Spacer size="16px" size1280="24px" />
                <Prose
                  className={Styles.Description}
                  {...(subTitle && {'data-has-subtitle': true})} // Adjust styling if there is a subheading
                  html={description}
                  ref={descriptionRef}
                />
              </>
            )}
          </div>

          {ctas && (
            <>
              <Spacer size="64px" size1280="0" />
              <div ref={ctaRef}>
                <CtaButtons contentfulContent={ctaGroup} location={location} />
              </div>
            </>
          )}
        </div>
      </div>

      <div className={Styles.Cell} ref={wrapper2Ref}>
        {media && (
          <div ref={assetRef} data-animate="container">
            <div data-animate="item">
              <MediaAsset content={media} location={location} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

try{ River.displayName ||= 'River' } catch {}